/**
 * Guards for /payment-result — blocks probe URLs like
 * /payment-result%7Chttps://www.dosttech.az/payment-result%7C...
 */

const CANONICAL_PATHS = new Set(["/payment-result", "/payment-result/"]);

const UNSAFE_PATH_RE = /%7C|\|/i;
const UNSAFE_QUERY_RE = /%7C|\||https?:\/\//i;

const MAX_QUERY_LENGTH = 4096;
const MAX_PARAM_COUNT = 64;
const MAX_PARAM_VALUE_LENGTH = 512;

/**
 * @returns {'bad-path'|'bad-query'|null}
 */
export function getPaymentResultViolation(pathname, search = "") {
  if (!pathname.startsWith("/payment-result")) {
    return null;
  }

  if (!CANONICAL_PATHS.has(pathname)) {
    return "bad-path";
  }

  if (UNSAFE_PATH_RE.test(pathname)) {
    return "bad-path";
  }

  if (!search || search === "?") {
    return null;
  }

  if (search.length > MAX_QUERY_LENGTH) {
    return "bad-query";
  }

  if (UNSAFE_QUERY_RE.test(search)) {
    return "bad-query";
  }

  try {
    const params = new URLSearchParams(
      search.startsWith("?") ? search.slice(1) : search
    );
    if ([...params.keys()].length > MAX_PARAM_COUNT) {
      return "bad-query";
    }
    for (const [, value] of params) {
      if (value.length > MAX_PARAM_VALUE_LENGTH) {
        return "bad-query";
      }
    }
  } catch {
    return "bad-query";
  }

  return null;
}

export function getPaymentResultViolationFromWindow() {
  if (typeof window === "undefined") return null;
  return getPaymentResultViolation(
    window.location.pathname,
    window.location.search
  );
}

/**
 * Run before React boot — avoids loading the SPA for garbage paths.
 * @returns {boolean} true if a redirect was performed
 */
export function rejectMalformedPaymentResultPath() {
  const violation = getPaymentResultViolationFromWindow();
  if (!violation) return false;

  const lang =
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("lang")) ||
    "az";
  window.location.replace(`/${lang}/`);
  return true;
}

/**
 * Bank/MPI query params only — strips anything suspicious.
 * @returns {URLSearchParams}
 */
export function getSanitizedPaymentResultSearchParams(search) {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  const out = new URLSearchParams();
  if (!raw) return out;

  try {
    const params = new URLSearchParams(raw);
    for (const [key, value] of params) {
      if (!key || key.length > 128) continue;
      if (UNSAFE_QUERY_RE.test(key) || UNSAFE_QUERY_RE.test(value)) continue;
      if (value.length > MAX_PARAM_VALUE_LENGTH) continue;
      out.append(key, value);
    }
  } catch {
    /* empty */
  }
  return out;
}
